import React, { useEffect } from 'react';
import { 
  Info, 
  AlertCircle, 
  Battery, 
  Clock, 
  Ruler, 
  Leaf, 
  MapPin, 
  Timer,
  ArrowUpDown
} from 'lucide-react';

const SensorDetails = ({ sensor, mostRecentData, onToggleDetailedData, showDetailedData }) => {
  useEffect(() => {
    // Hide detailed data when switching to a sensor with no data
    if (!mostRecentData && showDetailedData) {
      onToggleDetailedData();
    }
  }, [mostRecentData, showDetailedData, onToggleDetailedData, sensor.id]);

  if (!sensor) return null;
  
  const getTimeDifference = () => {
    if (!mostRecentData || !mostRecentData.timestamp) return 'N/A';
    const now = new Date();
    const dataTime = new Date(mostRecentData.timestamp);
    const diffInMinutes = Math.floor((now - dataTime) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minutes`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      return `${days} day${days > 1 ? 's' : ''}`;
    }
  };

  const getBatteryStatus = (voltage) => {
    if (!voltage) return { color: 'gray', text: 'Unknown' };
    if (voltage >= 3.6) return { color: 'green', text: 'Good' };
    if (voltage >= 3.3) return { color: 'yellow', text: 'Fair' };
    return { color: 'red', text: 'Low' };
  };
  
  const timeDiff = getTimeDifference();
  const batteryStatus = getBatteryStatus(mostRecentData?.battery_voltage);

  return (
    <div className="mb-6 bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-green-50 p-4 border-b border-green-100">
        <h3 className="text-xl font-semibold text-green-800">
          Sensor Details: {sensor.id}
        </h3>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Basic Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="flex items-start space-x-2">
            <Leaf className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
            <div>
              <p className="text-sm text-gray-500">Species</p>
              <p className="font-medium text-gray-900">{sensor.species || 'N/A'}</p>
            </div>
          </div>

          <div className="flex items-start space-x-2">
            <Ruler className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
            <div>
              <p className="text-sm text-gray-500">Tree Diameter</p>
              <p className="font-medium text-gray-900">{sensor.diameter || 'N/A'}</p>
            </div>
          </div>

          <div className="flex items-start space-x-2">
            <MapPin className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
            <div>
              <p className="text-sm text-gray-500">Location</p>
              <p className="font-medium text-gray-900">
                {sensor.coordinates ? sensor.coordinates.map(coord => coord.toFixed(6)).join(', ') : 'N/A'}
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-2">
            <Timer className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
            <div>
              <p className="text-sm text-gray-500">Measurement Interval</p>
              <p className="font-medium text-gray-900">
                {sensor.interval ? `${sensor.interval} minutes` : 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Latest Readings Section */}
        {mostRecentData ? (
          <div className="border rounded-lg p-4 bg-gray-50 mb-4">
            <h4 className="text-lg font-semibold text-gray-800 mb-3">Latest Readings</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Time Since Last Reading */}
              <div className="flex items-center space-x-2">
                <Clock className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="text-sm text-gray-500">Last Updated</p>
                  <p className="font-medium">
                    {timeDiff} ago
                  </p>
                </div>
              </div>

              {/* Battery Status */}
              <div className="flex items-center space-x-2">
                <Battery className="w-5 h-5" style={{ color: `var(--${batteryStatus.color}-500)` }} />
                <div>
                  <p className="text-sm text-gray-500">Battery Status</p>
                  <p className="font-medium">
                    {mostRecentData.battery_voltage?.toFixed(2) || 'N/A'} V ({batteryStatus.text})
                  </p>
                </div>
              </div>

              {/* Sap Flow Values */}
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                <div className="flex items-center space-x-2">
                  <ArrowUpDown className="w-5 h-5 text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-500">Uncorrected Inner Sap Flow</p>
                    <p className="font-medium">
                      {mostRecentData.uncorrected_inner?.toFixed(3) || 'N/A'} cm/hr
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <ArrowUpDown className="w-5 h-5 text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-500">Uncorrected Outer Sap Flow</p>
                    <p className="font-medium">
                      {mostRecentData.uncorrected_outer?.toFixed(3) || 'N/A'} cm/hr
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center space-x-2 text-yellow-600 bg-yellow-50 p-4 rounded-lg mb-4">
            <AlertCircle className="w-5 h-5" />
            <p>No recent data available for this sensor.</p>
          </div>
        )}

        {/* Info Note */}
        <div className="flex items-start space-x-2 text-gray-600 bg-blue-50 p-4 rounded-lg mb-4">
          <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <strong>About Sap Flow Measurements:</strong>
            <p>
              "Uncorrected inner" and "uncorrected outer" values show how much water is moving through 
              different parts of the tree's sapwood.
            </p>
          </div>
        </div>

        {/* Image and Action Button */}
        <div className="mt-4">
          <img 
            src={sensor.imageUrl} 
            alt={`Sensor ${sensor.id}`} 
            className="w-full max-w-[200px] h-auto object-cover rounded mb-2" 
          />
          {mostRecentData && (
            <button
              onClick={onToggleDetailedData}
              className="w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors duration-200 font-medium shadow-sm"
            >
              {showDetailedData ? "Hide Historical Data" : "Show Historical Data"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SensorDetails;